import {emphasize} from '@mui/material/styles';

/**
 * App specific overrides
 * @param {} theme
 */
export const getBaselineOverrides = theme => ({
  styleOverrides: {
    'html, body': {
      fontFamily: theme.typography.fontFamily,
      height: '100%',
      backgroundColor: theme.palette.paper,
    },
    '#pl-root': {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      height: '100%',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
    // style for shift-drag-zoom
    '.ol-box': {
      background: theme.palette.common.white,
      border: `1px dashed ${theme.palette.primary.dark}`,
      boxSizing: 'border-box',
      opacity: 0.4,
    },
    a: {
      cursor: 'pointer',
    },
    img: {
      userDrag: 'none',
    },
    '.intercom-launcher-frame': {
      display: 'none !important',
    },
    '.ReactVirtualized__List': {
      outline: 'none',
    },
    '.rdp': {
      fontFamily: theme.typography.fontFamily,
      '& .rdp-day_today': {
        fontWeight: 'normal',
      },
      '& .rdp-caption_label': {
        ...theme.typography.subtitle1,
      },
      '& .rdp-dropdown:focus:not([disabled]) + .rdp-caption_label': {
        border: 0,
        background: 'none',
      },
      '& .rdp-head_cell': {
        ...theme.typography.caption,
        textTransform: 'none',
      },

      '& .rdp-day': {
        outline: 'none',
        borderRadius: 4,
        '&:hover:not(.rdp-day_selected)': {
          border: '1px solid rgb(158, 158, 158)',
          backgroundColor: theme.palette.action.hover,
        },
      },
      '& .rdp-day_selected': {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.text.primary,
        '&:hover': {
          borderRadius: '4px',
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.action.hover,
        },
        '&:focus': {
          border: 'none',
          backgroundColor: theme.palette.primary.main,
        },
      },
      '& .rdp-day_range_start, .rdp-day_range_end': {
        borderRadius: '4px !important',
        fontWeight: 500,
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
          color: theme.palette.common.white,
          backgroundColor: theme.palette.primary.dark,
        },
      },
      '& .rdp-day_range_middle': {
        color: theme.palette.text.primary,
      },
    },
    '.sentry-error-embed-wrapper': {
      '& .btn': {
        background: `${theme.palette.primary.main} !important`,
        border: `1px solid ${theme.palette.primary.main} !important`,
        borderRadius: theme.shape.borderRadius,
      },
    },
  },
});

/**
 * Global and Material-UI overrides based on designs and pairing
 * We use the theme as a param to make the switch between dark and light easier
 * @param {*} theme - theme to be used as baseline
 */
export const getStyleOverrides = theme => {
  return {
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: theme.palette.grey[500],
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        sizeSmall: {
          padding: 7, // we want it square and matching material-ui spec
        },
      },
    },
    MuiLink: {
      defaultProps: {
        variant: 'inherit',
        underline: 'hover',
      },
    },
    MuiSlider: {
      styleOverrides: {
        markLabel: {
          top: 20,
        },
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
        shape: 'square',
        variant: 'light',
      },
      styleOverrides: {
        root: ({ownerState, theme}) => {
          const getColor = color =>
            theme.palette.mode === 'light' ? color.dark : color.light;
          return {
            ...(ownerState.variant === 'light' &&
              ownerState.color !== 'default' && {
                backgroundColor:
                  theme.palette[ownerState.color][theme.palette.mode],
                color: getColor(theme.palette[ownerState.color]),
                '&.MuiChip-clickable:hover': {
                  backgroundColor: emphasize(
                    theme.palette[ownerState.color][theme.palette.mode],
                    0.1
                  ),
                },
              }),
          };
        },
        icon: ({ownerState, theme}) => ({
          marginRight: ownerState.label
            ? theme.spacing(-0.5)
            : theme.spacing(0.5),
        }),
        label: ({ownerState, theme}) => ({
          paddingRight: ownerState.label ? theme.spacing(1) : 0,
          paddingLeft: ownerState.label ? theme.spacing(1) : 0,
        }),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-disabled+.MuiSwitch-track': {
            opacity: 1,
          },
        },
      },
    },
  };
};
